import type { LobbyClientMessage, LobbyEvent } from '@danda/shared';
import {
  WsCloseCodeEnum,
  WS_HEARTBEAT_INTERVAL_MS,
  WS_RECONNECT_DELAY_MS,
  MAX_QUEUED_MESSAGES,
  ActiveNPCEnum,
} from '@danda/shared';
import { useLobbyStore } from '@/lib/stores/lobbyStore';
import { auth } from '@/lib/firebase';
import { onIdTokenChanged, type Unsubscribe } from 'firebase/auth';
import { emitSessionExpired } from '@/lib/session-expired-events';
import { getDefaultLobbyWsUrl } from './websocket-url';
import { toast } from '@/lib/toast';
import { track } from '@/lib/analytics';
import { createLobbyCommandApi, type LobbyCommandApi } from './websocket-commands';
import { parseLobbyMessage } from './websocket-message-handler';
import { getStaffAvatarUrl } from '@/lib/media';
import { IS_DEV } from '@/lib/constants';

// Debug logging flag - only log in dev mode or when explicitly enabled
const WS_DEBUG = IS_DEV || import.meta.env.VITE_DEBUG_WS === 'true';

function wsLog(...args: unknown[]): void {
  if (WS_DEBUG) console.log('[WS]', ...args);
}

function wsWarn(...args: unknown[]): void {
  if (WS_DEBUG) console.warn('[WS]', ...args);
}

function wsError(...args: unknown[]): void {
  // Always log errors, but strip sensitive payload data in production
  if (WS_DEBUG) {
    console.error('[WS]', ...args);
  } else {
    // In production, log error type without potentially sensitive details
    console.error('[WS] Error occurred');
  }
}

// =============================================================================
// Types
// =============================================================================

interface WebSocketServiceConfig {
  url?: string;
  reconnectAttempts?: number;
  reconnectDelay?: number;
  heartbeatInterval?: number;
}

// =============================================================================
// Canned Aldric refusal — used when a player with a finalized character
// clicks Aldric. See sendAldricMessage in websocket-commands.ts.
// =============================================================================

const ALDRIC_REFUSAL_CONTENT =
  '*Brother Aldric lowers his quill with a gentle smile.* ' +
  'Your soul already walks its path, traveler — I can only guide those yet to find theirs. ' +
  'If your heart truly calls for a new hero, you must first let go of the one you carry. ' +
  'Speak with Tavian when you are ready to release them.';

let aldricRefusalMsgCounter = 0;
function showAldricExistingCharacterRefusal(): void {
  const state = useLobbyStore.getState();
  aldricRefusalMsgCounter += 1;
  state.addMessage('main', {
    id: `aldric-refusal-${Date.now()}-${aldricRefusalMsgCounter}`,
    senderId: 'aldric',
    senderName: 'Brother Aldric',
    content: ALDRIC_REFUSAL_CONTENT,
    timestamp: new Date(),
    type: 'aldric',
    avatarUrl: getStaffAvatarUrl('aldric-avatar.webp'),
  });
  state.setWaitingForResponse(false);
}

// =============================================================================
// WebSocket Service
// =============================================================================

// No longer needed - using WsCloseCodeEnum from @danda/shared

class WebSocketService implements LobbyCommandApi {
  private ws: WebSocket | null = null;
  private config: Required<WebSocketServiceConfig>;
  private reconnectCount = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  // Backoff credit is earned, not granted on open. A socket must stay open
  // for STABLE_CONNECTION_MS before reconnectCount resets, resetting in
  // onopen let a connect-then-drop cycle (instance churn, half-open, a
  // server that sends one frame then dies) reconnect every 2s forever
  // without ever reaching the attempt cap. Mirrors game-connection-manager.
  private stabilityTimer: ReturnType<typeof setTimeout> | null = null;
  private static readonly STABLE_CONNECTION_MS = 30_000;
  private joinTimer: ReturnType<typeof setTimeout> | null = null;
  // Set when max auto-reconnect retries are exhausted.
  // Blocks ALL automatic reconnections — only manual reconnect clears this.
  private autoReconnectDisabled = false;
  private messageQueue: LobbyClientMessage[] = [];
  private lobbyId: string | null = null;
  // Connection debouncing for React StrictMode (prevents double connections)
  private pendingConnect: Promise<void> | null = null;
  private connectionId = 0;
  // Token refresh listener
  private tokenUnsubscribe: Unsubscribe | null = null;
  private currentToken: string | null = null;
  // Close code 1000 disambiguation (C2): true when close was initiated by client
  private intentionalClose = false;
  // Rapid-cycle handshake-failure detection: server accepts the WS upgrade
  // then closes before sending any message (cold-start saturation, half-open
  // edge, post-deploy churn). Soft-stuck escape only catches "stuck CONNECTING";
  // this catches "opened then closed without messages." After
  // HANDSHAKE_FAILURE_LIMIT consecutive such cycles we trip reconnectFailed
  // so LobbyLayout's existing DisconnectedOverlay surfaces with a manual retry.
  private handshakeFailures = 0;
  private firstMsgAfterOpen = false;
  private static readonly HANDSHAKE_FAILURE_LIMIT = 3;
  // Persistent auth-reject escape: a successful Firebase getIdToken(true) does
  // NOT prove the lobby server will accept the refreshed token. On a server-side
  // auth rejection that keeps firing, refresh-then-reconnect would loop forever
  // (each cycle used to reset reconnectCount to 0, so the 5-attempt cap was never
  // reached and no recovery UI ever surfaced). Bound it: after
  // AUTH_FAILURE_LIMIT consecutive auth closes, stop auto-reconnecting and
  // surface the same recovery UI as the transient-failure cap. Reset alongside
  // handshakeFailures once a connection genuinely stabilizes.
  private authFailures = 0;
  private static readonly AUTH_FAILURE_LIMIT = 3;
  subscribeLobby!: LobbyCommandApi['subscribeLobby'];
  unsubscribeLobby!: LobbyCommandApi['unsubscribeLobby'];
  joinLobby!: LobbyCommandApi['joinLobby'];
  leaveLobby!: LobbyCommandApi['leaveLobby'];
  setReady!: LobbyCommandApi['setReady'];
  sendTavianMessage!: LobbyCommandApi['sendTavianMessage'];
  sendChatMessage!: LobbyCommandApi['sendChatMessage'];
  startCharacterCreation!: LobbyCommandApi['startCharacterCreation'];
  startCharacterCreationSession!: LobbyCommandApi['startCharacterCreationSession'];
  sendAldricMessage!: LobbyCommandApi['sendAldricMessage'];
  cancelCharacterCreation!: LobbyCommandApi['cancelCharacterCreation'];
  startLevelUp!: LobbyCommandApi['startLevelUp'];
  continueLevelUp!: LobbyCommandApi['continueLevelUp'];
  sendKiraMessage!: LobbyCommandApi['sendKiraMessage'];
  startMerchantSession!: LobbyCommandApi['startMerchantSession'];
  sendGroltMessage!: LobbyCommandApi['sendGroltMessage'];
  closeMerchantSession!: LobbyCommandApi['closeMerchantSession'];
  startCharacterChat!: LobbyCommandApi['startCharacterChat'];
  closeCharacterChat!: LobbyCommandApi['closeCharacterChat'];
  addFriendFromCharChat!: LobbyCommandApi['addFriendFromCharChat'];
  requestFriendsList!: LobbyCommandApi['requestFriendsList'];
  removeFriend!: LobbyCommandApi['removeFriend'];
  startFriendDm!: LobbyCommandApi['startFriendDm'];
  closeFriendDm!: LobbyCommandApi['closeFriendDm'];
  createParty!: LobbyCommandApi['createParty'];
  joinParty!: LobbyCommandApi['joinParty'];
  leaveParty!: LobbyCommandApi['leaveParty'];
  voteOnDecision!: LobbyCommandApi['voteOnDecision'];
  acceptPartyInvite!: LobbyCommandApi['acceptPartyInvite'];
  declinePartyInvite!: LobbyCommandApi['declinePartyInvite'];
  setPartyPacing!: LobbyCommandApi['setPartyPacing'];
  startGame!: LobbyCommandApi['startGame'];
  quickStartAdventure!: LobbyCommandApi['quickStartAdventure'];
  requestBackground!: LobbyCommandApi['requestBackground'];
  requestPortrait!: LobbyCommandApi['requestPortrait'];
  startTrainingDuel!: LobbyCommandApi['startTrainingDuel'];
  setChatFocus!: LobbyCommandApi['setChatFocus'];
  constructor(config: WebSocketServiceConfig = {}) {
    this.config = {
      url: config.url || this.getDefaultUrl(),
      reconnectAttempts: config.reconnectAttempts ?? 5,
      reconnectDelay: config.reconnectDelay ?? WS_RECONNECT_DELAY_MS,
      heartbeatInterval: config.heartbeatInterval ?? WS_HEARTBEAT_INTERVAL_MS,
    };

    const commands = createLobbyCommandApi({
      send: (message) => this.send(message),
      getLobbyId: () => this.lobbyId,
      startAldricChat: () => useLobbyStore.getState().startAldricChat(),
      startKiraChat: () => useLobbyStore.getState().startKiraChat(),
      startGroltChat: () => useLobbyStore.getState().startGroltChat(),
      getSelectedCharacterId: () => useLobbyStore.getState().selectedCharacterId,
      getCharCreationCharId: () => useLobbyStore.getState().charCreationCharId,
      setWaitingForResponse: (waiting) => useLobbyStore.getState().setWaitingForResponse(waiting),
      showAldricExistingCharacterRefusal: () => showAldricExistingCharacterRefusal(),
    });
    Object.assign(this, commands);

    // Start listening for token changes to handle refresh
    this.startTokenListener();
  }

  /**
   * Listen for Firebase ID token changes and reconnect with fresh token
   */
  private startTokenListener(): void {
    // Clean up any existing listener
    this.stopTokenListener();

    this.tokenUnsubscribe = onIdTokenChanged(auth, async (user) => {
      if (user) {
        try {
          const newToken = await user.getIdToken();
          // If token changed and we're connected, reconnect with new token
          if (this.currentToken && this.currentToken !== newToken && this.isConnected()) {
            wsLog('Token refreshed, reconnecting with new token');
            this.currentToken = newToken;
            // Force reconnect to use new token
            this.forceReconnect();
          } else {
            this.currentToken = newToken;
          }
        } catch (error) {
          wsError('Failed to get refreshed token:', error);
        }
      } else {
        // User signed out - clear token and tear down the connection. Gate on
        // the socket/attempt EXISTING, not on isConnected() (which is OPEN-only):
        // a socket still CONNECTING at sign-out would otherwise survive, open
        // under the old account's token, and be reused by the next account's
        // connect() (the readyState===OPEN reuse guard), leaking one account's
        // authenticated lobby session — and friends list — into another.
        this.currentToken = null;
        if (this.ws || this.pendingConnect) {
          wsLog('User signed out, disconnecting');
          this.disconnect();
        }
      }
    });
  }

  private stopTokenListener(): void {
    if (this.tokenUnsubscribe) {
      this.tokenUnsubscribe();
      this.tokenUnsubscribe = null;
    }
  }

  /**
   * Force reconnection (used when token is refreshed)
   */
  private forceReconnect(): void {
    if (this.ws) {
      this.intentionalClose = true;
      this.ws.close(WsCloseCodeEnum.NORMAL_CLOSURE, 'Token refresh');
      this.ws = null;
    }
    // Immediate reconnect with new token
    this.reconnectCount = 0; // Reset count since this is intentional
    this.connect(this.lobbyId || undefined).catch((error) => {
      wsError('Force reconnect failed:', error);
    });
  }

  // ===========================================================================
  // Connection Management
  // ===========================================================================

  /**
   * Connect to the lobby WebSocket server
   * Uses debouncing to prevent double connections from React StrictMode
   */
  async connect(lobbyId?: string): Promise<void> {
    // Debounce: if connection already in progress, return that promise
    // This handles React StrictMode double-renders and rapid reconnects
    if (this.pendingConnect) {
      wsLog('Connection already in progress, returning existing promise');
      return this.pendingConnect;
    }

    // Check if already connected
    if (this.ws?.readyState === WebSocket.OPEN) {
      wsLog('Already connected, skipping connect');
      return;
    }

    // We're past the already-open guard, so no socket is currently delivering
    // chunks — any lobby bubble still flagged live is an orphan from a prior
    // (now-dead) connection whose NpcStreamEnd can never arrive. Settle them and
    // drop the stale stream registry BEFORE the new socket opens. Gating this on
    // the old `wasReconnecting` flag missed the dashboard→lobby remount path
    // (`disconnect()` resets `reconnectCount`), leaving an orphan that spins
    // @llm-ui's reveal rAF forever → unbounded BlockMatch allocation → OOM.
    useLobbyStore.setState({ activeStreams: {}, completedStreamIds: {} });
    useLobbyStore.getState().finalizeStreamingMessages();

    // Track this connection attempt
    const thisConnectionId = ++this.connectionId;

    // If lobby context changes, drop any queued messages tied to old lobby
    if (this.lobbyId && lobbyId && lobbyId !== this.lobbyId) {
      this.clearMessageQueue();
    }
    this.lobbyId = lobbyId || 'default';
    wsLog('Connecting to lobby:', this.lobbyId, 'connectionId:', thisConnectionId);

    // Create the connection promise with cleanup. The finally must only
    // clear ITS OWN attempt: disconnect() nulls pendingConnect mid-flight,
    // and a follow-up connect() may have installed a fresh attempt by the
    // time this stale one settles.
    const attempt = this.doConnect(thisConnectionId).finally(() => {
      if (this.pendingConnect === attempt) {
        this.pendingConnect = null;
      }
    });
    this.pendingConnect = attempt;

    return attempt;
  }

  /**
   * Internal connection implementation
   */
  private async doConnect(connectionId: number): Promise<void> {
    this.intentionalClose = false;
    // Get auth token if available
    const token = await this.getAuthToken();

    // Check if this connection attempt was superseded while getting token
    if (this.connectionId !== connectionId) {
      wsLog('Connection superseded while getting auth token');
      throw new Error('Connection superseded');
    }

    const url = new URL(this.config.url);
    if (this.lobbyId) {
      url.searchParams.set('lobbyId', this.lobbyId);
    }

    wsLog('Creating WebSocket connection to:', url.toString());

    return new Promise<void>((resolve, reject) => {
      // Double-check we haven't been superseded
      if (this.connectionId !== connectionId) {
        wsLog('Connection superseded before WebSocket creation');
        reject(new Error('Connection superseded'));
        return;
      }
      // The inner Promise MUST be settled exactly once. If the WS opens →
      // resolve. If it closes or errors before opening → reject. Without
      // this guard, a "close before open" path (Cloud Run cold start, server
      // overload, network blip) leaves the Promise pending forever, which
      // pins `pendingConnect` in `connect()` and turns every subsequent
      // reconnect attempt into a no-op (returns the dead Promise). The user
      // sees "Opening Portal..." indefinitely with no escape.
      let settled = false;
      const settle = (fn: () => void) => {
        if (settled) return;
        settled = true;
        fn();
      };
      // Send 'bearer' and token as separate protocols - server expects them comma-separated in header
      const protocols = token ? ['bearer', token] : undefined;
      const newWs = protocols
        ? new WebSocket(url.toString(), protocols)
        : new WebSocket(url.toString());
      this.ws = newWs;

      newWs.onopen = () => {
        // Verify this is still the active connection
        if (this.ws !== newWs) {
          wsLog('Connection opened but superseded by newer connection, closing');
          newWs.close();
          return;
        }

        wsLog('Connection opened');
        const wasReconnecting = this.reconnectCount > 0;
        this.firstMsgAfterOpen = false;
        // Reset the attempt counter only once THIS socket proves stable
        // (see stabilityTimer field comment). A stale fire is a no-op.
        this.clearStabilityTimer();
        this.stabilityTimer = setTimeout(() => {
          this.stabilityTimer = null;
          if (this.ws === newWs && newWs.readyState === WebSocket.OPEN) {
            this.reconnectCount = 0;
            this.handshakeFailures = 0;
            this.authFailures = 0;
          }
        }, WebSocketService.STABLE_CONNECTION_MS);
        useLobbyStore.getState().setOnline(true);
        useLobbyStore.getState().setLobbyId(this.lobbyId);
        useLobbyStore.getState().setError(null); // Clear any previous errors

        // Stale streams are cleared + orphan bubbles finalized at the START of
        // connect() now (covers the dashboard→lobby remount that resets
        // `reconnectCount`), so nothing to do here on open.
        if (wasReconnecting) {
          toast.success('Reconnected', {
            description: 'Connection restored',
          });
        }

        // Always join and subscribe to lobby when connection opens
        // This ensures we're properly connected even on reconnects
        if (this.lobbyId) {
          wsLog('Auto-joining lobby on connection (sync):', this.lobbyId);
          this.send({ type: 'lobby:join', lobbyId: this.lobbyId });
          this.send({ type: 'lobby:subscribe', lobbyId: this.lobbyId });

          // Hydrate the account-level friends list on every socket generation,
          // not just on an isOnline false→true edge. A token-refresh
          // `forceReconnect()` swaps the socket while `isOnline` stays true, so an
          // effect keyed on `isOnline` would never refetch; requesting here keeps
          // the list fresh across reconnects and self-heals a dropped initial one.
          this.requestFriendsList();

          // Resume Aldric/Kira session if that's where the user was
          // Note: Chat history is persisted in localStorage, so user can see previous messages
          // The backend session is re-initialized but Aldric/Kira will greet fresh
          const state = useLobbyStore.getState();
          if (state.mainChatNpc === ActiveNPCEnum.ALDRIC && wasReconnecting) {
            wsLog('Resuming Aldric session on reconnect');
            // In-memory reconnect keeps the rendered chat, so hasHistory=true:
            // the server sends a no-content stream (just clears the spinner)
            // rather than re-streaming a prompt the client already shows.
            this.startCharacterCreationSession(true);
          } else if (
            state.mainChatNpc === ActiveNPCEnum.KIRA &&
            wasReconnecting &&
            state.selectedCharacterId
          ) {
            wsLog('Resuming Kira session on reconnect');
            this.startLevelUp(state.selectedCharacterId);
          } else if (
            state.mainChatNpc === ActiveNPCEnum.GROLT &&
            wasReconnecting &&
            state.selectedCharacterId
          ) {
            wsLog('Resuming Grolt session on reconnect');
            this.startMerchantSession(state.selectedCharacterId);
          }
        }

        // Flush queued messages AFTER join/subscribe so the server has
        // registered the client before processing chat messages, votes, etc.
        this.flushMessageQueue();

        settle(resolve);
      };

      newWs.onclose = (event) => {
        // Only handle close for the active connection. CRITICAL: even when
        // superseded (e.g. client called disconnect() and nulled this.ws),
        // we MUST still settle the inner Promise — otherwise pendingConnect
        // stays non-null forever and every subsequent connect() call returns
        // a dead Promise. That's the "stuck on Opening Portal until reload"
        // bug. Settle first, then early-return for superseded.
        if (this.ws !== newWs) {
          wsLog('Ignoring close for superseded connection (settling Promise)');
          settle(() => reject(new Error(`WS superseded (close code=${event.code})`)));
          return;
        }

        wsLog('Connection closed', { code: event.code, reason: event.reason });
        this.clearStabilityTimer();
        useLobbyStore.getState().setOnline(false);

        // Settle the inner Promise so doConnect's `.finally()` clears
        // `pendingConnect` before any scheduled reconnect attempts run.
        // Without this, close-before-open leaves doConnect pending forever
        // and every retry hits the pendingConnect debounce → no-op.
        settle(() => reject(new Error(`WS closed before open: code=${event.code}`)));

        // Handle auth-related close codes - clear queue and force re-auth
        const isAuthError =
          event.code === WsCloseCodeEnum.UNAUTHORIZED ||
          event.code === WsCloseCodeEnum.FORBIDDEN ||
          event.code === WsCloseCodeEnum.TOKEN_EXPIRED;
        const isSignedOutElsewhere = event.code === WsCloseCodeEnum.SIGNED_OUT_ELSEWHERE;

        if (isAuthError) {
          wsLog('Auth error close code, clearing queue and forcing re-auth');
          this.clearMessageQueue();
          this.authFailures += 1;
          // A successful Firebase refresh doesn't prove the server accepts the
          // token, so a persistent server-side rejection must not loop forever.
          // After AUTH_FAILURE_LIMIT consecutive auth closes, give up and
          // surface the same recovery UI as the transient-failure cap
          // (DisconnectedOverlay via reconnectFailed) plus the SessionExpired
          // overlay, instead of refreshing + reconnecting again.
          if (this.authFailures >= WebSocketService.AUTH_FAILURE_LIMIT) {
            wsWarn(
              `Auth close ${this.authFailures}× — server keeps rejecting the token; surfacing recovery UI`
            );
            this.autoReconnectDisabled = true;
            useLobbyStore.getState().setReconnectFailed(true);
            emitSessionExpired('lobby-ws-auth-persistent-reject');
            return;
          }
          // Below the limit: the normal "token just expired" case. Refresh once
          // and schedule a single reconnect. Per Inv 3, refresh failure does NOT
          // call signOut — emit a session-expired event so the global overlay
          // can attempt its own silent refresh; the user keeps their session
          // unless they explicitly click "Sign out" in the overlay. Do NOT reset
          // reconnectCount here: granting backoff credit from a mere token
          // refresh let the auth path dodge the earned-backoff cap forever.
          auth.currentUser
            ?.getIdToken(true)
            .then((newToken) => {
              this.currentToken = newToken;
              // Same supersede guard as the .catch arm below: a stale success
              // landing after disconnect()/a fresh connect() must not arm a
              // reconnect timer that reopens a zombie lobby socket on whatever
              // screen the user moved to. Caching the fresh token is harmless.
              if (this.ws === newWs) {
                this.scheduleReconnect();
              }
            })
            .catch((error) => {
              wsError('Failed to refresh token after auth close:', error);
              // The socket is closed and no reconnect is armed. Without the
              // recovery-UI flags, a later successful refresh (the overlay's
              // silent retry, or the user's Retry) fires onIdTokenChanged but
              // the token listener only reconnects OPEN sockets, so the closed
              // socket is orphaned: dead lobby, recoverable only by reload.
              // Surface the same manual-recovery UI as the persistent-reject
              // and transient-exhaustion caps so DisconnectedOverlay renders a
              // working Reconnect button.
              //
              // Guard against a stale rejection: getIdToken(true) can still be
              // in flight when disconnect()/a fresh connect() supersede this
              // socket (it nulls or replaces this.ws and resets
              // autoReconnectDisabled). Re-arming the flags here would clobber
              // the new connection's recovery state (sticky autoReconnectDisabled
              // survives a plain connect()). Same supersede test onclose uses.
              if (this.ws === newWs) {
                this.autoReconnectDisabled = true;
                useLobbyStore.getState().setReconnectFailed(true);
                emitSessionExpired('lobby-ws-auth-refresh-failed');
              }
            });
          return;
        }

        if (isSignedOutElsewhere) {
          wsLog('Session taken over by another tab; redirecting to dashboard');
          this.clearMessageQueue();
          this.stopTokenListener();
          this.reconnectCount = this.config.reconnectAttempts; // disable reconnects
          useLobbyStore.getState().reset();
          // Persist message for dashboard to display after redirect
          sessionStorage.setItem('session-moved', 'lobby');
          window.location.href = '/dashboard';
          return;
        }

        if (event.code === WsCloseCodeEnum.INSUFFICIENT_TOKENS) {
          wsLog('Insufficient PM tokens; redirecting to dashboard');
          this.clearMessageQueue();
          this.stopTokenListener();
          this.reconnectCount = this.config.reconnectAttempts; // disable reconnects
          useLobbyStore.getState().reset();
          window.location.href = '/dashboard';
          return;
        }

        // Intentional close from client (L4) — don't retry
        if (event.code === WsCloseCodeEnum.NORMAL_CLOSURE && this.intentionalClose) {
          this.intentionalClose = false;
          return;
        }

        // Server-initiated 1000 (server restart) (L5) — treat as transient
        const isTransient =
          event.code !== WsCloseCodeEnum.NORMAL_CLOSURE ||
          (event.code === WsCloseCodeEnum.NORMAL_CLOSURE && !this.intentionalClose);

        this.intentionalClose = false;

        // Rapid-cycle escape: socket opened but closed before any message
        // landed. Count it; after the limit, stop retrying and surface the
        // existing DisconnectedOverlay (gated on reconnectFailed && !isOnline).
        if (isTransient && !this.firstMsgAfterOpen) {
          this.handshakeFailures += 1;
          if (this.handshakeFailures >= WebSocketService.HANDSHAKE_FAILURE_LIMIT) {
            wsWarn(
              `Handshake failed ${this.handshakeFailures}× without messages — surfacing manual reconnect`
            );
            this.autoReconnectDisabled = true;
            useLobbyStore.getState().setReconnectFailed(true);
            return;
          }
        }

        if (isTransient && this.reconnectCount < this.config.reconnectAttempts) {
          this.scheduleReconnect();
          toast.warning('Connection Lost', {
            description: 'Attempting to reconnect...',
          });
        } else if (isTransient) {
          // Connection lost and no more reconnect attempts (L8)
          // Block auto-reconnect — only manual reconnect can clear this
          this.autoReconnectDisabled = true;
          useLobbyStore.getState().setReconnectFailed(true);
        }
      };

      newWs.onerror = (error) => {
        // Same Promise-leak rule as onclose: settle even when superseded.
        if (this.ws !== newWs) {
          wsLog('Ignoring error for superseded connection (settling Promise)');
          settle(() => reject(new Error('WS superseded (error)')));
          return;
        }

        wsError('WebSocket error:', error);
        track('app_error', {
          message: 'WebSocket connection error',
          name: 'ws_error',
          context: 'lobby_ws',
        });
        const errorMessage = 'Connection error - please check your internet connection';
        useLobbyStore.getState().setError(errorMessage);
        toast.error('Connection Error', {
          description: errorMessage,
        });
        settle(() => reject(error));
      };

      newWs.onmessage = (event) => {
        // Only handle messages for the active connection
        if (this.ws !== newWs) {
          return;
        }
        this.handleMessage(event.data);
      };
    });
  }

  /**
   * Disconnect from the WebSocket server
   */
  private clearStabilityTimer(): void {
    if (this.stabilityTimer) {
      clearTimeout(this.stabilityTimer);
      this.stabilityTimer = null;
    }
  }

  disconnect(): void {
    wsLog('disconnect() called');
    this.clearStabilityTimer();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.joinTimer) {
      clearTimeout(this.joinTimer);
      this.joinTimer = null;
    }
    if (this.ws) {
      this.intentionalClose = true;
      this.ws.close(WsCloseCodeEnum.NORMAL_CLOSURE, 'Client disconnect');
      this.ws = null;
    }
    // CRITICAL: clear pendingConnect explicitly. The onclose handler now
    // settles the inner Promise on supersede (see above), but the .finally()
    // on the wrapping Promise can race with browser close-event timing.
    // Bumping connectionId invalidates the stale attempt's superseded check,
    // and nulling pendingConnect lets the next connect() start fresh.
    this.connectionId += 1;
    this.pendingConnect = null;
    this.autoReconnectDisabled = false;
    this.reconnectCount = 0;
    this.handshakeFailures = 0;
    this.authFailures = 0;
    this.firstMsgAfterOpen = false;
    this.clearMessageQueue();
    useLobbyStore.getState().setOnline(false);
  }

  /**
   * Logout from the lobby server
   * Sends explicit logout message to clean up server-side sessions before disconnecting
   */
  logout(): void {
    wsLog('logout() called');

    // Send logout message to server (for immediate session cleanup)
    if (this.ws?.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(JSON.stringify({ type: 'lobby:logout' }));
        wsLog('Logout message sent to server');
      } catch (error) {
        wsError('Failed to send logout message:', error);
      }
    }

    // Then disconnect
    this.disconnect();
    this.clearMessageQueue();
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  // ===========================================================================
  // Message Sending
  // ===========================================================================

  /**
   * Send a message to the server
   */
  private send(message: LobbyClientMessage): void {
    const payload = JSON.stringify(message);
    wsLog('send called', { type: message.type, readyState: this.ws?.readyState });
    if (this.ws?.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(payload);
        wsLog('message sent successfully', { type: message.type });
      } catch (error) {
        wsError('send failed:', error);
        this.messageQueue.push(message);
      }
    } else {
      // Queue message for when connection is restored
      this.messageQueue.push(message);
      // Prevent unbounded growth in pathological offline cases
      if (this.messageQueue.length > MAX_QUEUED_MESSAGES) {
        const dropped = this.messageQueue.length - MAX_QUEUED_MESSAGES;
        this.messageQueue = this.messageQueue.slice(-MAX_QUEUED_MESSAGES);
        wsWarn(`Queue overflow: dropped ${dropped} oldest message(s)`);
      }
      wsWarn('WebSocket not connected, message queued', {
        type: message.type,
        readyState: this.ws?.readyState,
      });
    }
  }

  /**
   * Send queued messages after reconnection
   */
  private flushMessageQueue(): void {
    while (this.messageQueue.length > 0) {
      const message = this.messageQueue.shift();
      if (message && this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify(message));
      }
    }
  }

  private clearMessageQueue(): void {
    this.messageQueue = [];
  }

  // ===========================================================================
  // Message Handling
  // ===========================================================================

  /**
   * Handle incoming WebSocket message
   */
  private handleMessage(data: string): void {
    if (!this.firstMsgAfterOpen) {
      this.firstMsgAfterOpen = true;
      this.handshakeFailures = 0;
      this.authFailures = 0;
    }
    const event = parseLobbyMessage(data);
    if (!event) return;

    wsLog('Received event:', event.type);
    try {
      useLobbyStore.getState().handleLobbyEvent(event as LobbyEvent);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      const errorStack = error instanceof Error ? error.stack : undefined;
      wsError('Error handling lobby event; dropping', {
        errorMessage,
        errorStack,
        eventType: event.type,
      });
    }
  }

  // ===========================================================================
  // Reconnection
  // ===========================================================================

  private scheduleReconnect(): void {
    if (this.autoReconnectDisabled) return;
    if (this.reconnectTimer) return;
    if (this.reconnectCount >= this.config.reconnectAttempts) {
      wsWarn('Max reconnect attempts reached, not scheduling further reconnects');
      this.autoReconnectDisabled = true;
      // Surface the recovery UI on ANY exhaustion path. The transient branch
      // sets this itself before it would ever reach here, but the auth branch
      // calls scheduleReconnect() directly and can arrive with reconnectCount
      // already at the cap (inherited from prior transient flaps). Without this,
      // a below-AUTH_FAILURE_LIMIT auth close at the cap would disable reconnects
      // silently, leaving the player offline with no DisconnectedOverlay.
      useLobbyStore.getState().setReconnectFailed(true);
      return;
    }

    this.reconnectCount++;
    const delay = Math.min(
      30000,
      this.config.reconnectDelay * Math.pow(2, this.reconnectCount - 1)
    );

    this.reconnectTimer = setTimeout(async () => {
      this.reconnectTimer = null;
      try {
        await this.connect(this.lobbyId || undefined);
      } catch (error) {
        wsError('Reconnect failed:', error);
      }
    }, delay);
  }

  // ===========================================================================
  // Helpers
  // ===========================================================================

  private getDefaultUrl(): string {
    return getDefaultLobbyWsUrl();
  }

  private async getAuthToken(): Promise<string | null> {
    try {
      const user = auth.currentUser;
      if (user) {
        return await user.getIdToken();
      }
    } catch (error) {
      wsWarn('Failed to get auth token:', error);
    }
    return null;
  }

  /**
   * Set custom WebSocket URL
   */
  setUrl(url: string): void {
    this.config.url = url;
  }

  /**
   * Manual reconnect attempt (called from UI after reconnectFailed)
   * Resets reconnect counter and tries to connect
   */
  async manualReconnect(): Promise<void> {
    wsLog('Manual reconnect requested');
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.autoReconnectDisabled = false;
    this.reconnectCount = 0;
    this.handshakeFailures = 0;
    this.authFailures = 0;
    this.firstMsgAfterOpen = false;
    useLobbyStore.getState().setReconnectFailed(false);
    try {
      await this.connect(this.lobbyId || undefined);
    } catch (error) {
      wsError('Manual reconnect failed:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const websocketService = new WebSocketService();
export default websocketService;

// Force full page reload on HMR for this module since it's a singleton
// that holds WebSocket connection state
if (import.meta.hot) {
  import.meta.hot.accept(() => {
    wsLog('HMR detected, disconnecting...');
    websocketService.disconnect();
    // Force page reload since we can't hot-replace a singleton's state
    window.location.reload();
  });
}
